import ApiService from './api';

const TeasersService = {
  /**
   * @param botId Bot the widget belongs to
   * @param channelId Channel whose teaser configuration is fetched
   * @returns Teaser management data, `{}` when the response has no body, or `null` on error
   */
  async getTeasers(botId: string, channelId: string): Promise<any> {
    try {
      const url = `/widget/teaser/management/${botId}/${channelId}`;

      const response = await ApiService.get(url);
      if (response.data) {
        return response.data;
      }

      return {};
    } catch (error) {
      return null;
    }
  },
  /**
   * Persists the full teaser configuration of a channel.
   * @returns Backend response, `{}` when empty, or `null` on error
   */
  async saveTeasers({
    botId,
    channelId,
    teasers,
  }: {
    /** Bot the widget belongs to */
    botId: string;
    /** Channel whose teaser configuration is saved */
    channelId: string;
    /** Complete teaser configuration to store (shape managed by the teaser editor) */
    teasers: any;
  }): Promise<any> {
    try {
      const url = `/widget/teaser/management/${botId}/${channelId}`;

      const response = await ApiService.post(url, {
        teaser: teasers,
      });
      if (response.data) {
        return response.data;
      }

      return {};
    } catch (error) {
      return null;
    }
  },
  /**
   * Asks the API which of the given teasers would be played out on a URL.
   *
   * The matching itself deliberately stays in the API, on the same code path the
   * live widget request uses, so this can never drift from what really happens.
   * The endpoint is stateless: it reads no bot and no channel, we hand it both
   * the URL and the candidates.
   *
   * @param params.url The URL to test, as typed by the user
   * @param params.isMobile
   * @param params.teasers Candidates, in priority order. Only the fields that
   *  matching needs are sent.
   * @returns `null` when the request failed
   */
  async testUrlMatch({
    url,
    isMobile,
    teasers,
  }: {
    url: string;
    isMobile: boolean;
    teasers: any[];
  }): Promise<any> {
    try {
      const response = await ApiService.post('/widget/teaser/match-test', {
        url,
        isMobile,
        teasers: (teasers || []).map((t) => ({
          id: t.id,
          active: t.active,
          device: t.device,
          allowList: t.allowList,
          blockList: t.blockList,
          ignoreTrailingSlash: t.ignoreTrailingSlash,
          timeConstraints: t.timeConstraints,
        })),
      });

      return response.data || null;
    } catch (error: any) {
      // Input validation answers with 400 and the same `{ error: { code } }` shape
      if (error?.response?.data?.error) return error.response.data;
      return null;
    }
  },
  /**
   * @param botId Bot the teaser belongs to
   * @param teaserId Teaser whose statistics are requested
   * @param dateRange Two-element `[start, end]` range (date strings or timestamps), serialized into query params
   * @returns Stats payload, `{}` when empty, or `null` on error
   */
  async getTeaserStats(
    botId: string,
    teaserId: string,
    dateRange: (string | number)[]
  ): Promise<any> {
    try {
      const url = `/widget/teaser/${botId}/${teaserId}/stats?start=${dateRange[0]}&end=${dateRange[1]}`;
      const response = await ApiService.get(url);
      if (response.data) {
        return response.data;
      }

      return {};
    } catch (error) {
      return null;
    }
  },
};

export default TeasersService;
